import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  GraduationCap,
  MapPin,
  PhoneCall,
  Users,
  HeartHandshake,
  School,
  MessagesSquare,
  Phone,
} from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { Marquee } from "../components/Marquee";
import { FaqAccordion } from "../components/FaqAccordion";
import { ReviewCard } from "../components/ReviewCard";
import { MapBlock } from "../components/MapBlock";
import { CtaBanner } from "../components/CtaBanner";
import { Placeholder } from "../components/Placeholder";
import {
  site,
  images,
  programs,
  trustItems,
  whyChooseUs,
  reviews,
  homeFaqs,
  galleryImages,
  btnPrimary,
  btnSecondary,
} from "../config/siteConfig";

const iconMap = { GraduationCap, MapPin, PhoneCall, Users, HeartHandshake, School, MessagesSquare };

const MaskedLine = ({ children, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="mask-line pb-1">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
};

const Hero = () => {
  const heroRef = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={heroRef} data-testid="hero-section" className="grid-bg relative overflow-hidden">
      <div
        className="pointer-events-none absolute -left-32 top-16 h-80 w-80 rounded-full bg-brand-light"
        aria-hidden="true"
      />
      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 pb-16 pt-12 sm:px-6 lg:grid-cols-2 lg:pb-24 lg:pt-20">
        <div>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-brand"
          >
            <span className="h-2 w-2 rounded-full bg-brand-amber" aria-hidden="true" />
            Serving Mundeke Goraya & Surrounding Communities
          </motion.p>
          <h1 className="mt-6 font-display text-[2.5rem] font-extrabold leading-[1.05] tracking-tight text-brand-ink sm:text-6xl lg:text-[4rem]">
            <MaskedLine delay={0.1}>A Place to Learn,</MaskedLine>
            <MaskedLine delay={0.22}>Grow & Build a</MaskedLine>
            <MaskedLine delay={0.34}>
              <span className="text-brand">Brighter Future</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-brand-muted sm:text-lg"
          >
            Ansar Public School is a local educational institution serving families in
            Mundeke Goraya and surrounding communities. Explore our school, learn about
            admissions and get in touch with our team.
          </motion.p>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-8 flex flex-col gap-3 sm:flex-row"
          >
            <Link to="/admissions" data-testid="hero-admission-cta" className={btnPrimary}>
              Enquire About Admission
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/about" data-testid="hero-explore-cta" className={btnSecondary}>
              Explore Our School
            </Link>
          </motion.div>
          <motion.a
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            href={site.phoneHref}
            data-testid="hero-phone-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand transition-colors hover:text-brand-dark"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            Call us directly — {site.phoneDisplay}
          </motion.a>
        </div>

        <motion.div style={reduce ? undefined : { y: imgY }} className="relative">
          <div
            className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full border-[10px] border-brand-amber/70"
            aria-hidden="true"
          />
          <motion.div
            initial={reduce ? false : { clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="spotlight-frame rounded-[2rem]"
          >
            <img
              src={images.hero}
              alt="Teacher presenting a lesson to engaged students in a classroom"
              width="800"
              height="600"
              fetchpriority="high"
              className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-2xl"
            />
          </motion.div>
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="absolute -bottom-6 left-4 flex items-center gap-3 rounded-2xl border border-brand-line bg-white px-5 py-4 shadow-xl sm:left-8"
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-light">
              <GraduationCap className="h-5 w-5 text-brand" aria-hidden="true" />
            </span>
            <span>
              <span className="block font-display text-sm font-bold text-brand-ink">
                Admission Enquiries
              </span>
              <span className="block text-xs text-brand-muted">
                Speak directly with the school team
              </span>
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <>
      <Seo
        title="Ansar Public School Mundeke Goraya | School in Daska"
        description="Learn about Ansar Public School in Mundeke Goraya, Daska, Sialkot. Explore school information, admissions, location and contact details."
      />
      <Hero />
      <Marquee />

      <section data-testid="trust-strip" className="border-b border-brand-line bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-4 px-4 py-8 sm:px-6 lg:grid-cols-4">
          {trustItems.map((item, i) => {
            const Icon = iconMap[item.icon];
            return (
              <Reveal key={item.label} delay={i * 0.08} className="h-full">
                <div
                  data-testid={`trust-item-${i}`}
                  className="flex h-full items-center gap-3 rounded-2xl border border-brand-line bg-brand-cream px-4 py-4"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-light">
                    <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold text-brand-ink">{item.label}</span>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="home-about" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal>
              <SectionHeading
                chapter="01"
                eyebrow="Our School"
                title="Learning That Starts With the Right Environment"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base leading-relaxed text-brand-muted sm:text-lg">
                Every student's educational journey begins with the right environment.
                Ansar Public School is committed to providing a welcoming space where
                students can learn, develop confidence and build a strong foundation for
                the future.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link to="/about" data-testid="home-about-cta" className={`${btnSecondary} mt-7`}>
                Learn About Our School
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="spotlight-frame rounded-[2rem]">
              <img
                src={images.about}
                alt="Teacher guiding young students during a classroom activity"
                loading="lazy"
                width="800"
                height="600"
                className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-programs" className="border-y border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <Reveal>
            <SectionHeading
              chapter="02"
              eyebrow="Academic Programs"
              title="Programs for Every Stage of Learning"
              description="Program details below are placeholders until confirmed by the school. Contact the school office for verified class information."
            />
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {programs.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08} className="h-full">
                <article
                  data-testid={`program-card-${p.id}`}
                  className="flex h-full flex-col gap-3 rounded-3xl border border-brand-line bg-brand-cream p-6 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <span className="font-display text-sm font-extrabold text-brand-amber">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-xl font-bold text-brand-ink">{p.title}</h3>
                  <Placeholder>{p.text}</Placeholder>
                  <p className="mt-auto pt-2 text-xs text-brand-muted">
                    Details to be confirmed with the school office.
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="home-why-us" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <Reveal>
          <SectionHeading
            chapter="03"
            eyebrow="Why Choose Us"
            title="A School Built Around Students and Families"
            align="center"
          />
        </Reveal>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {whyChooseUs.map((w, i) => {
            const Icon = iconMap[w.icon];
            return (
              <Reveal key={w.title} delay={i * 0.08} className="h-full">
                <article
                  data-testid={`why-card-${i}`}
                  className="flex h-full flex-col gap-3 rounded-3xl border border-brand-line bg-white p-6 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand">
                    <Icon className="h-6 w-6 text-white" aria-hidden="true" />
                  </span>
                  <h3 className="font-display text-lg font-bold text-brand-ink">{w.title}</h3>
                  <p className="text-sm leading-relaxed text-brand-muted">{w.text}</p>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section data-testid="home-gallery-preview" className="border-y border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <Reveal>
            <SectionHeading
              chapter="04"
              eyebrow="Campus Life"
              title="See Our Learning Environment"
              align="center"
            />
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
            {galleryImages.slice(0, 6).map((img, i) => (
              <Reveal key={img.src} delay={i * 0.06}>
                <Link
                  to="/gallery"
                  data-testid={`gallery-preview-${i}`}
                  className="group block overflow-hidden rounded-2xl border border-brand-line focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                  aria-label={`View gallery — ${img.alt}`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    width="800"
                    height="600"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/gallery" data-testid="gallery-view-all-link" className={btnSecondary}>
              View Full Gallery
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <CtaBanner
          idPrefix="home-admissions"
          title="Ready to Explore Admission?"
          text="Speak with our school team to learn about available classes, admission requirements, timings and the next steps."
          primaryLabel="Start an Admission Enquiry"
          primaryTo="/admissions"
          secondaryLabel="Call the School"
          secondaryHref={site.phoneHref}
        />
      </section>

      <section data-testid="home-reviews" className="border-y border-brand-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <Reveal>
            <SectionHeading
              chapter="05"
              eyebrow="Reviews"
              title="What Families Say"
              description="We never publish made-up reviews. Verified reviews from families will appear here once shared."
              align="center"
            />
          </Reveal>
          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={i} delay={i * 0.08} className="h-full">
                <ReviewCard review={r} index={i} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/reviews" data-testid="reviews-view-all-link" className={btnSecondary}>
              Visit the Reviews Page
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-faq" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
        <Reveal>
          <SectionHeading
            chapter="06"
            eyebrow="FAQs"
            title="Questions Parents Often Ask"
            align="center"
          />
        </Reveal>
        <Reveal delay={0.1} className="mx-auto mt-10 max-w-3xl">
          <FaqAccordion faqs={homeFaqs} testIdPrefix="home-faq" />
        </Reveal>
        <Reveal className="mt-8 text-center">
          <Link to="/faqs" data-testid="faqs-view-all-link" className={btnSecondary}>
            See All FAQs
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Reveal>
      </section>

      <section data-testid="home-location" className="border-t border-brand-line bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <div>
            <Reveal>
              <SectionHeading
                chapter="07"
                eyebrow="Location"
                title="Find Ansar Public School"
              />
            </Reveal>
            <Reveal delay={0.1}>
              <address className="mt-6 space-y-1 not-italic text-base leading-relaxed text-slate-700">
                <p>{site.addressLine1},</p>
                <p>{site.addressLine2},</p>
                <p>{site.addressLine3}</p>
              </address>
              <p className="mt-4 flex items-center gap-2 text-base font-semibold text-brand">
                <Phone className="h-4 w-4" aria-hidden="true" />
                <a href={site.phoneHref} data-testid="location-phone-link" className="hover:underline">
                  {site.phoneDisplay}
                </a>
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <MapBlock />
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20">
        <CtaBanner
          idPrefix="home-final"
          title="Take the Next Step for Your Child's Education"
          text="Have questions about admissions, classes or the school environment? Contact Ansar Public School to learn more."
          primaryLabel="Send an Enquiry"
          primaryTo="/contact"
          secondaryLabel="Call the School"
          secondaryHref={site.phoneHref}
        />
      </section>
    </>
  );
}
